import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { apiRequest } from '../lib/utils';
import { isPushSupported, isPushSubscribed, subscribeToPush } from '../utils/pushNotifications';
import { setCustomRoles } from '../config/accessControl';

const AuthContext = createContext(null);

// Auto-enable push notifications for logged-in users
const autoEnablePushNotifications = async (token) => {
  if (!isPushSupported()) {
    console.log('Push notifications not supported in this browser');
    return;
  }
  
  // Check if already subscribed
  const alreadySubscribed = await isPushSubscribed();
  if (alreadySubscribed) {
    console.log('Push notifications already enabled');
    return;
  }
  
  // Check if permission was previously denied
  if (Notification.permission === 'denied') {
    console.log('Push notifications were denied by user');
    return;
  }
  
  try {
    // Request permission and subscribe
    await subscribeToPush(token);
    console.log('Push notifications auto-enabled successfully');
  } catch (error) {
    // Silent fail - don't disrupt user experience
    console.log('Push notifications setup skipped:', error.message);
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    // Initialize from localStorage immediately to prevent flash
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const storedToken = localStorage.getItem('token');
    if (!storedToken) {
      setLoading(false);
      return;
    }

    setToken(storedToken);

    try {
      const userData = await apiRequest('/auth/me');
      // Detect server-driven role change so the user knows why their sidebar
      // suddenly changed.
      const cached = localStorage.getItem('user');
      const cachedRole = cached ? (JSON.parse(cached)?.role) : null;
      if (cachedRole && userData?.role && cachedRole !== userData.role) {
        toast.info(
          `Your role was updated to "${userData.role.replace(/_/g, ' ')}". Sidebar and permissions have been refreshed.`,
          { duration: 8000 }
        );
      }
      setUser(userData);
      // Update stored user data
      localStorage.setItem('user', JSON.stringify(userData));

      // Hydrate the custom-roles registry so the sidebar can render
      // custom roles without a redeploy.
      try {
        const { roles } = await apiRequest('/access-control/custom-roles');
        setCustomRoles(roles || []);
      } catch (_) { /* best effort */ }
      
      // Auto-enable push notifications for returning users
      setTimeout(() => autoEnablePushNotifications(storedToken), 2000);
    } catch (error) {
      console.error('Failed to load user:', error);
      // Only clear if token is truly invalid (401 errors)
      // Keep user logged in for network errors
      if (error.status === 401 || error.message === 'Unauthorized' || error.message === 'Token expired') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setToken(null);
        setUser(null);
      }
      // For network errors, keep the cached user data
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // Auto-refresh user data every 2 minutes so admin-driven role changes
  // propagate to active sessions without forcing the user to re-login.
  useEffect(() => {
    if (!token) return;
    const id = setInterval(() => {
      loadUser();
    }, 2 * 60 * 1000);
    return () => clearInterval(id);
  }, [token, loadUser]);

  const login = async (email, password) => {
    const response = await apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    localStorage.setItem('token', response.token);
    localStorage.setItem('user', JSON.stringify(response.user));
    setToken(response.token);
    setUser(response.user);
    
    // Auto-enable push notifications after login
    setTimeout(() => autoEnablePushNotifications(response.token), 1000);
    
    return response;
  };

  const signup = async (userData) => {
    const response = await apiRequest('/auth/signup', {
      method: 'POST',
      body: JSON.stringify(userData),
    });

    // New flow: signup returns { pending_approval: true } and NO token.
    // The user can't log in until an admin approves them.
    if (response?.pending_approval) {
      return response;
    }

    // Legacy/backward-compat path: if a token comes back (e.g., admin-created),
    // log the user in.
    if (response?.token) {
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      setToken(response.token);
      setUser(response.user);
      setTimeout(() => autoEnablePushNotifications(response.token), 1000);
    }

    return response;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Clear cached per-user access overrides so a fresh login doesn't see
    // the previous user's grants/blocks during the brief window before the
    // overrides hook refetches.
    try { sessionStorage.removeItem('pie:access-overrides:v2'); } catch (_) { /* ignore */ }
    setToken(null);
    setUser(null);
  };

  // Canonical admin roles — must mirror backend core/auth.py require_admin.
  const ADMIN_ROLES = [
    'ceo', 'md', 'tech_support',
    'operations_manager', 'franchise_manager',
    'hr_manager',
    'accounts_manager', 'accounts_payables',
    'purchase_manager', 'warehouse_manager', 'warehouse_admin',
    'store_manager',
    'office_administrator',
  ];
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const isOperationsManager = user?.role === 'operations_manager';

  // isTopManagement: Only CEO and MD have FULL control (Admin Panel, Settings, User Management)
  const isTopManagement = user?.role === 'ceo' || user?.role === 'md';

  return (
    <AuthContext.Provider value={{ user, token, loading, login, signup, logout, isAdmin, isTopManagement, isOperationsManager, loadUser, refreshUser: loadUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
